"use client";

import { useEffect } from "react";

// Browsers throw a SecurityError once history.replaceState/pushState is called
// >100 times / 10s. Next's AppRouter syncs the canonical URL via replaceState on
// every URL change, so a runaway render/nav loop can trip that cap and crash the
// whole app. We wrap both methods, keep a 10s sliding window, and once calls
// cross a soft limit we drop further calls (staying under the browser cap) and
// report the URL sequence once per window — capturing the real culprit instead
// of an opaque SecurityError.

const WINDOW_MS = 10_000;
const SOFT_LIMIT = 80; // stay under the browser's 100/10s cap
const MAX_RECENT_URLS = 30;

export default function HistoryGuard() {
  useEffect(() => {
    if (typeof window === "undefined" || !window.history) return;

    const history = window.history;
    const originalReplaceState = history.replaceState.bind(history);
    const originalPushState = history.pushState.bind(history);

    let callTimestamps: number[] = [];
    let recentUrls: string[] = [];
    let lastReportAt = 0;

    const report = (kind: string) => {
      const now = Date.now();
      if (now - lastReportAt < WINDOW_MS) return; // one diagnostic per window
      lastReportAt = now;
      try {
        fetch("/api/report-error", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            source: "history.guard",
            name: "HistoryLoopGuard",
            message: `history.${kind} exceeded ${SOFT_LIMIT} calls / ${WINDOW_MS / 1000}s — throttling to avoid a SecurityError crash.`,
            details: JSON.stringify({
              recentUrls: recentUrls.slice(-MAX_RECENT_URLS),
              callsInWindow: callTimestamps.length,
            }),
            stack: new Error("history.guard trip").stack,
            path: window.location.pathname,
            autoReloaded: false,
          }),
          keepalive: true,
        }).catch(() => {});
      } catch {
        // swallow reporter transport errors
      }
    };

    const guarded =
      (kind: "replaceState" | "pushState", original: History["replaceState"]) =>
      (data: unknown, unused: string, url?: string | URL | null) => {
        const now = Date.now();
        callTimestamps = callTimestamps.filter((t) => now - t < WINDOW_MS);
        callTimestamps.push(now);
        if (url != null) {
          recentUrls.push(String(url));
          if (recentUrls.length > MAX_RECENT_URLS) recentUrls = recentUrls.slice(-MAX_RECENT_URLS);
        }
        if (callTimestamps.length > SOFT_LIMIT) {
          report(kind);
          return; // drop — keeps us under the browser's hard cap
        }
        return original(data, unused, url as string);
      };

    history.replaceState = guarded("replaceState", originalReplaceState) as History["replaceState"];
    history.pushState = guarded("pushState", originalPushState) as History["pushState"];

    return () => {
      history.replaceState = originalReplaceState;
      history.pushState = originalPushState;
    };
  }, []);

  return null;
}
